define((require) => {
    "use strict";

    const _             = require("underscore");
    const Backbone      = require("backbone");
    const Preconditions = require("shared/util/Preconditions");

    /**
     * Base class for a collection that contains polymorphic models. <p>
     *
     * The model type is determined by the "type" property on each model. Declare a "modelTypes" hash mapping the type
     * values to model classes when extending this collection <b>or</b> define "modelTypes" to be an enum with an
     * optional "modelClass" property to specify how to find the actual type name of the deduced enum type. <p>
     *
     * In addition, the "defaultModelType" property can be specified to use in case no such type is found. <p>
     *
     * Enum examples:
     * <pre>
     *    ..
     *    modelTypes: WidgetTypes,   // use WidgetTypes enum
     *    modelClass: "modelClass",  // and get the actual WidgetType class from the "modelClass" property on the enum constant
     *    ..
     *
     *    ..
     *    modelTypes: Actions,       // here the Actions enum is actually an enum of the types, so no need for "modelClass"
     *    ..
     * </pre>
     *
     * @class PolymorphicCollection
     * @author Bruce Mackenzie Nielsen
     */
    return Backbone.Collection.extend({

        /**
         * Factory method (and not a Model constructor) to return the proper polymorphic type. Actually invoked
         * as "new model(attributes, options)", where "model" is this function. Hence, not in scope of this
         * collection.
         */
        model(attributes, options) {
            if (attributes instanceof Backbone.Model || _.isArray(attributes)) {
                return attributes;
            }

            const type     = attributes.type;
            const types    = options.collection.modelTypes;
            const map      = _.isFunction(types) ? types.call(this) : types;
            const typesMap = map.__enumName ? map.lookup(type, true) : map[type]; // normal [] lookup may also work for enums, but lookup is more powerful
            const prop     = options.collection.modelClass;
            const Model    = prop ? typesMap[prop] : typesMap;

            // Make sure we can handle the type, i.e. do not explode if unknown, but silently discard.
            Preconditions.checkArgument(Model, `Unknown polymorphic type: '${(options.collection.__typeName || "?")}.${type}'`);

            if (Model) {
                return new Model(attributes, options);
            }
            // The Backbone.Collection always expects a model to be returned, so we just fake it by returning
            // an "empty model" with a validate function that will always return false, causing the model to
            // be discarded eventually by the collection. However, if the polymorphic collection has a default type,
            // it will be used...
            return {
                _validate() {
                    return false;
                }
            };
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/zbone/PolymorphicCollection.js